// B6 — labiq's author-facing registry proxies (RegistryController), envelopes pinned against the
// REAL controller (feature-tested in RegistryProxyTest). FAIL CLOSED, resolved as outcomes: a 503
// or an unrecognised body is `unavailable`, never an empty result and never a thrown transport
// error a caller might swallow into "no data" (the EX-lane rule).
import { ENV } from "../../config/runtimeEnv";
import axios from "axios";

import { RegistryGene, RegistrySearchOutcome, RegistryVariant, RegistryVariantLookup } from "../../types/geneCodes/RegistryTypes";
import { http } from "../../lib/http/client";

// Already ends in `/api` — the GxEApi rule; a second /api 404s every call.
const API_BASE_URL = ENV.VITE_LABIQ_SERVER || "";

export interface RegistrySearchParams {
	query: string;
	/** Variants only — mapped onto the registry's gene_symbol_lc filter server-side. */
	gene?: string;
	page?: number;
	limit?: number;
}

const search = async <T>(collection: "genes" | "variants", params: RegistrySearchParams): Promise<RegistrySearchOutcome<T>> => {
	try {
		const response = await http.get(`${API_BASE_URL}/registry/${collection}`, {
			params: {
				query: params.query,
				...(params.gene ? { gene: params.gene } : {}),
				...(params.page != null ? { page: params.page } : {}),
				...(params.limit != null ? { limit: params.limit } : {}),
			},
		});
		const data = response.data?.data;
		// An unrecognised 200 is treated as an outage, never as an empty picker.
		if (!data || !Array.isArray(data.results) || typeof data.total !== "number") return { status: "unavailable" };
		return { status: "ok", results: data.results as T[], total: data.total };
	} catch {
		return { status: "unavailable" };
	}
};

// The two search verbs take a positional query OR the R7 params object; `limit` stays second so the
// autocomplete's existing searchRegistryGenes(query, LIMIT) call is untouched.
const paramsFrom = (query: string | RegistrySearchParams, limit?: number): RegistrySearchParams =>
	typeof query === "string" ? { query, ...(limit != null ? { limit } : {}) } : query;

/** GET /registry/genes?query=&limit=&page= — query required server-side; limit 1-100, default 10. */
export const searchRegistryGenes = (query: string | RegistrySearchParams, limit?: number): Promise<RegistrySearchOutcome<RegistryGene>> =>
	search<RegistryGene>("genes", paramsFrom(query, limit));

/** GET /registry/variants?query=&gene=&limit=&page= — the same results-list envelope as genes. `total` is the REGISTRY's count, not the page's. */
export const searchRegistryVariants = (query: string | RegistrySearchParams, limit?: number): Promise<RegistrySearchOutcome<RegistryVariant>> =>
	search<RegistryVariant>("variants", paramsFrom(query, limit));

/** GET /registry/variants/{rs} — rs travels VERBATIM (the server lowercases); 404 = absent from the corpus, NOT an outage. */
export const fetchRegistryVariant = async (rs: string): Promise<RegistryVariantLookup> => {
	try {
		const response = await http.get(`${API_BASE_URL}/registry/variants/${encodeURIComponent(rs)}`);
		const doc = response.data?.data;
		if (!doc || typeof doc !== "object") return { status: "unavailable" };
		return { status: "found", doc: doc as RegistryVariant };
	} catch (error) {
		if (axios.isAxiosError(error) && error.response?.status === 404) return { status: "not_found" };
		return { status: "unavailable" };
	}
};
